/**
 * @file Grille spatiale uniforme (hash de cellules) : insertion/mise a jour/suppression, requetes cercle, plus proche, segment.
 */
import type { EntityId } from '@shared/types';
import { dist2, pointSegmentDist } from '../math/geometry';

interface GridItem {
  id: EntityId;
  x: number;
  y: number;
  r: number;
  cells: number[];
}

/** Cle de cellule 32 bits (cx, cy dans [-32768, 32767]). */
const cellKey = (cx: number, cy: number): number => ((cx & 0xffff) << 16) | (cy & 0xffff);

export class SpatialGrid {
  private readonly cells = new Map<number, Set<EntityId>>();
  private readonly items = new Map<EntityId, GridItem>();
  private readonly scratch = new Set<EntityId>();

  constructor(readonly cellSize: number) {}

  get size(): number {
    return this.items.size;
  }

  has(id: EntityId): boolean {
    return this.items.has(id);
  }

  clear(): void {
    this.cells.clear();
    this.items.clear();
  }

  private cellsFor(x: number, y: number, r: number, out: number[]): number[] {
    out.length = 0;
    const cs = this.cellSize;
    const x0 = Math.floor((x - r) / cs);
    const x1 = Math.floor((x + r) / cs);
    const y0 = Math.floor((y - r) / cs);
    const y1 = Math.floor((y + r) / cs);
    for (let cx = x0; cx <= x1; cx++) for (let cy = y0; cy <= y1; cy++) out.push(cellKey(cx, cy));
    return out;
  }

  insert(id: EntityId, x: number, y: number, r: number): void {
    if (this.items.has(id)) {
      this.update(id, x, y, r);
      return;
    }
    const item: GridItem = { id, x, y, r, cells: [] };
    this.cellsFor(x, y, r, item.cells);
    for (const k of item.cells) {
      let s = this.cells.get(k);
      if (!s) {
        s = new Set();
        this.cells.set(k, s);
      }
      s.add(id);
    }
    this.items.set(id, item);
  }

  update(id: EntityId, x: number, y: number, r?: number): void {
    const item = this.items.get(id);
    if (!item) return;
    const rr = r ?? item.r;
    const cs = this.cellSize;
    const sameCells =
      Math.floor((item.x - item.r) / cs) === Math.floor((x - rr) / cs) &&
      Math.floor((item.x + item.r) / cs) === Math.floor((x + rr) / cs) &&
      Math.floor((item.y - item.r) / cs) === Math.floor((y - rr) / cs) &&
      Math.floor((item.y + item.r) / cs) === Math.floor((y + rr) / cs);
    item.x = x;
    item.y = y;
    item.r = rr;
    if (sameCells) return;
    this.unlinkCells(item);
    this.cellsFor(x, y, rr, item.cells);
    for (const k of item.cells) {
      let s = this.cells.get(k);
      if (!s) {
        s = new Set();
        this.cells.set(k, s);
      }
      s.add(id);
    }
  }

  remove(id: EntityId): void {
    const item = this.items.get(id);
    if (!item) return;
    this.unlinkCells(item);
    this.items.delete(id);
  }

  private unlinkCells(item: GridItem): void {
    for (const k of item.cells) {
      const s = this.cells.get(k);
      if (!s) continue;
      s.delete(item.id);
      if (s.size === 0) this.cells.delete(k);
    }
  }

  /** Appelle `cb` une fois par element dont le cercle touche le cercle (x, y, r). Ordre deterministe (id croissant). */
  queryCircle(x: number, y: number, r: number, cb: (id: EntityId, d2: number) => void): void {
    const found = this.collect(x - r, y - r, x + r, y + r);
    for (const id of found) {
      const it = this.items.get(id);
      if (!it) continue;
      const d2 = dist2(x, y, it.x, it.y);
      const rr = r + it.r;
      if (d2 <= rr * rr) cb(id, d2);
    }
  }

  /** Element le plus proche dans le rayon `maxR` satisfaisant `filter` ; egalite departagee par id croissant. */
  nearest(x: number, y: number, maxR: number, filter: (id: EntityId) => boolean): EntityId | null {
    let best: EntityId | null = null;
    let bestD = Number.POSITIVE_INFINITY;
    this.queryCircle(x, y, maxR, (id, d2) => {
      if (d2 < bestD && filter(id)) {
        bestD = d2;
        best = id;
      }
    });
    return best;
  }

  /** Elements dont le cercle intersecte un segment epais (faisceau). Trie par distance depuis A. */
  querySegment(ax: number, ay: number, bx: number, by: number, halfWidth: number): EntityId[] {
    const pad = halfWidth + this.cellSize;
    const found = this.collect(Math.min(ax, bx) - pad, Math.min(ay, by) - pad, Math.max(ax, bx) + pad, Math.max(ay, by) + pad);
    const hits: Array<{ id: EntityId; d: number }> = [];
    for (const id of found) {
      const it = this.items.get(id);
      if (!it) continue;
      if (pointSegmentDist(it.x, it.y, ax, ay, bx, by) <= halfWidth + it.r) hits.push({ id, d: dist2(ax, ay, it.x, it.y) });
    }
    hits.sort((p, q) => p.d - q.d || p.id - q.id);
    return hits.map((h) => h.id);
  }

  /** Ids candidats dans une boite englobante, tries pour le determinisme. */
  private collect(x0: number, y0: number, x1: number, y1: number): EntityId[] {
    const cs = this.cellSize;
    this.scratch.clear();
    const cx0 = Math.floor(x0 / cs);
    const cx1 = Math.floor(x1 / cs);
    const cy0 = Math.floor(y0 / cs);
    const cy1 = Math.floor(y1 / cs);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cy = cy0; cy <= cy1; cy++) {
        const s = this.cells.get(cellKey(cx, cy));
        if (s) for (const id of s) this.scratch.add(id);
      }
    }
    return Array.from(this.scratch).sort((a, b) => a - b);
  }
}
